/**
 * Resolve a UOM name for display, treating blank/whitespace-only and "-"
 * (backend's placeholder for "no UOM set") as missing, not just falsy values.
 */
export const resolveUomName = (
  uomName: string | null | undefined,
  fallback: string,
): string => {
  const trimmed = (uomName || '').trim();
  return trimmed && trimmed !== '-' ? trimmed : fallback;
};

// -------- Indian-numbering (lakh/crore) number-to-words --------
const NUM_ONES = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
];
const NUM_TEENS = [
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];
const NUM_TENS = [
  '',
  '',
  'Twenty',
  'Thirty',
  'Forty',
  'Fifty',
  'Sixty',
  'Seventy',
  'Eighty',
  'Ninety',
];

function convertLessThanThousand(n: number): string {
  if (n === 0) return '';
  if (n < 10) return NUM_ONES[n];
  if (n < 20) return NUM_TEENS[n - 10];
  if (n < 100)
    return (
      NUM_TENS[Math.floor(n / 10)] +
      (n % 10 !== 0 ? ' ' + NUM_ONES[n % 10] : '')
    );
  return (
    NUM_ONES[Math.floor(n / 100)] +
    ' Hundred' +
    (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '')
  );
}

/**
 * Converts a non-negative integer to words using the Indian numbering system
 * (crore/lakh/thousand).
 */
const numberToWords = (num: number): string => {
  if (num === 0) return 'Zero';

  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const remainder = num % 1000;

  let result = '';
  if (crore > 0) result += convertLessThanThousand(crore) + ' Crore ';
  if (lakh > 0) result += convertLessThanThousand(lakh) + ' Lakh ';
  if (thousand > 0) result += convertLessThanThousand(thousand) + ' Thousand ';
  if (remainder > 0) result += convertLessThanThousand(remainder);

  return result.trim();
};

/** Converts a currency amount to "Rupees ... and Paise ... Only" (Indian numbering). */
export const amountInWords = (amount: number): string => {
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);

  let words = numberToWords(rupees) + ' Rupees';
  if (paise > 0) {
    words += ' and ' + numberToWords(paise) + ' Paise';
  }
  return words + ' Only';
};
